import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { User, Package, Heart, LogOut } from 'lucide-react';
import Header from '../components/Header';
import { useAccess } from '../context/AccessContext';
import { BRAND } from '../data/brand';

const MOCK_ORDERS = [
  {
    id: 'ORD-S13-00847',
    date: 'Mar 28, 2026',
    items: ['Secret Nº1 (3.5g)', 'Secret Nº4 (7g)'],
    total: '$527.25',
    status: 'Delivered',
  },
  {
    id: 'ORD-S13-00691',
    date: 'Mar 12, 2026',
    items: ['Secret Nº2 (3.5g)'],
    total: '$175.00',
    status: 'Delivered',
  },
  {
    id: 'ORD-S13-00423',
    date: 'Feb 20, 2026',
    items: ['Secret Nº7 (14g)', 'Secret Nº3 (3.5g)'],
    total: '$787.50',
    status: 'Delivered',
  },
];

const MOCK_FAVORITES = [
  { id: 'secret-13-n1', name: 'Secret Nº1', subtitle: 'The Night Secret' },
  { id: 'secret-13-n4', name: 'Secret Nº4', subtitle: 'The Signature Secret' },
  { id: 'secret-13-n7', name: 'Secret Nº7', subtitle: 'The Calm Secret' },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { memberEmail, revokeAccess } = useAccess();
  const [activeTab, setActiveTab] = useState('orders');

  const handleLogout = () => {
    revokeAccess();
    navigate('/');
  };

  const tabs = [
    { id: 'orders', label: 'Order History', icon: Package },
    { id: 'favorites', label: 'Favorites', icon: Heart },
    { id: 'account', label: 'Account', icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Member
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-4"
            data-testid="profile-heading"
          >
            Your Profile
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500"
          >
            {memberEmail || 'member@secret13.com'}
          </motion.p>
        </div>
      </section>

      {/* Tabs */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto flex justify-center gap-8 border-b border-white/5 pb-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 pb-4 font-sans text-xs tracking-[0.15em] uppercase transition-all duration-300 border-b-2 ${
                  activeTab === tab.id
                    ? 'text-white border-white'
                    : 'text-zinc-500 border-transparent hover:text-zinc-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-4"
            >
              {MOCK_ORDERS.map((order, index) => (
                <div
                  key={order.id}
                  className="border border-white/5 p-6 hover:border-white/10 transition-all duration-300"
                  data-testid={`order-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-mono text-xs text-zinc-400">{order.id}</p>
                      <p className="font-sans text-[10px] text-zinc-600 mt-1">{order.date}</p>
                    </div>
                    <span className="font-sans text-[10px] tracking-[0.15em] uppercase text-zinc-400 border border-white/10 px-3 py-1">
                      {order.status}
                    </span>
                  </div>
                  <div className="mb-3">
                    {order.items.map((item) => (
                      <p key={item} className="font-sans text-sm text-zinc-300">{item}</p>
                    ))}
                  </div>
                  <p className="font-sans text-sm text-zinc-400">{order.total}</p>
                </div>
              ))}
            </motion.div>
          )}

          {/* Favorites Tab */}
          {activeTab === 'favorites' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-4"
            >
              {MOCK_FAVORITES.map((fav, index) => (
                <div
                  key={fav.id}
                  onClick={() => navigate(`/product/${fav.id}`)}
                  className="flex items-center justify-between border border-white/5 p-6 cursor-pointer hover:border-white/15 transition-all duration-300"
                  data-testid={`favorite-${index}`}
                >
                  <div>
                    <p className="font-serif text-lg text-zinc-100">{fav.name}</p>
                    <p className="font-sans text-xs text-zinc-500">{fav.subtitle}</p>
                  </div>
                  <Heart className="w-4 h-4 text-zinc-500" />
                </div>
              ))}
            </motion.div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-6"
            >
              <div className="border border-white/5 p-6">
                <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-2">Email</p>
                <p className="font-sans text-sm text-zinc-300">{memberEmail || 'member@secret13.com'}</p>
              </div>
              <div className="border border-white/5 p-6">
                <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-2">Membership</p>
                <p className="font-sans text-sm text-zinc-300">Bronze (Free)</p>
                <button
                  onClick={() => navigate('/membership')}
                  className="font-sans text-xs text-zinc-500 hover:text-white mt-2 transition-colors"
                >
                  Upgrade membership
                </button>
              </div>
              <div className="border border-white/5 p-6">
                <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-2">Reward Points</p>
                <p className="font-sans text-sm text-zinc-300">0 points</p>
                <button
                  onClick={() => navigate('/rewards')}
                  className="font-sans text-xs text-zinc-500 hover:text-white mt-2 transition-colors"
                >
                  Earn rewards
                </button>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center gap-2 text-zinc-500 hover:text-red-400 font-sans text-xs tracking-[0.15em] uppercase transition-colors mt-8"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default ProfilePage;
